// THE ROUTE AN INVOICE WILL TAKE, AND ITS OVERRIDE, IN WORDS (owner,
// 2026-10-12 — docs/decisions.md D-350).
//
// The editor shows one line beside Release (Reinstate at Denial Review), in
// bold, then a link of its own (amended, 2026-10-12): "**This invoice will
// require Controller Approval and Director Approval.** Click here to
// override." The Override pop-up opens on what the route follows now, in the owner's words: "Currently, this invoice is following the routing
// rule:...", "Currently, this invoice requires all applicable approvals from
// two applicable rules:...", or "Currently, this invoice is following the
// precedent set by a prior override:...".
//
// Pure, so the tests read the same words the screen shows.

import type { DecisionRoutingRule, OversightRule, RequiredApprovals, RouteOverride, RoutingPreview } from '../api/types'
import { formatDateTime } from './format'
import { andList, requiredApprovalNames, routingRuleSentenceWithApprovals } from './oversightRuleSentence'

/** The steps a route requires as a list, or "no approvals". */
export function routeList(flags: RequiredApprovals): string {
  const names = requiredApprovalNames(flags)
  return names.length === 0 ? 'no approvals' : andList(names)
}

/** The line beside Release: "This invoice will require Controller Approval and Director Approval." */
export function routeNotice(flags: RequiredApprovals): string {
  return `This invoice will require ${routeList(flags)}.`
}

/** The four flags of a stored Override. */
export function overrideApprovals(override: RequiredApprovals): RequiredApprovals {
  return {
    requires_controller: override.requires_controller,
    requires_manager: override.requires_manager,
    requires_director: override.requires_director,
    requires_oversight: override.requires_oversight,
  }
}

/**
 * The route the next Release or Reinstate will take (D-350): the invoice's
 * Override when one is set, else what the rules or the default route say.
 * Null while the preview has not answered.
 */
export function effectiveRoute(override: RouteOverride | null, preview: RoutingPreview | null): RequiredApprovals | null {
  if (override !== null) return overrideApprovals(override)
  return preview === null ? null : preview.required
}

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']

/** A rule quoted as its History sentence, its own closing period dropped. */
function quoted(rule: DecisionRoutingRule): string {
  return `"${routingRuleSentenceWithApprovals(rule).replace(/\.$/, '').replace(/"/g, "'")}"`
}

/**
 * THE POP-UP'S OPENING: what the route follows now, as a lead sentence and the
 * rules it names. The Override on the invoice, when one is set, comes first;
 * otherwise the rules the preview found, or the default route.
 */
export function currentRouteExplanation(
  override: RouteOverride | null,
  preview: RoutingPreview | null,
): { lead: string; rules: string[] } {
  if (override !== null) {
    const by = override.set_by_name?.trim() ? ` set by ${override.set_by_name.trim()}` : ''
    return {
      lead: `Currently, this invoice is following an override${by}, which requires ${routeList(override)}.`,
      rules: [],
    }
  }
  if (preview === null) return { lead: 'Currently, this invoice’s route is being worked out.', rules: [] }
  const rules = preview.rules
  if (rules.length === 0) {
    return {
      lead: `Currently, no routing rule applies, so this invoice is following the default route, which requires ${routeList(preview.required)}.`,
      rules: [],
    }
  }
  if (rules.length === 1) {
    const rule = rules[0]!
    return {
      lead:
        rule.source === 'override'
          ? 'Currently, this invoice is following the precedent set by a prior override:'
          : 'Currently, this invoice is following the routing rule:',
      rules: [quoted(rule)],
    }
  }
  const count = NUMBER_WORDS[rules.length] ?? String(rules.length)
  return {
    lead: `Currently, this invoice requires all applicable approvals from ${count} applicable rules, which together require ${routeList(preview.required)}:`,
    rules: rules.map(quoted),
  }
}

/** The link after the route line, a sentence of its own (D-350 amended). */
export const OVERRIDE_LINK = 'Click here to override.'

/** The checkbox's own words (owner, 2026-10-12, amended the same day), set to
    the LEFT of the box. */
export const CREATE_RULE_LABEL = 'Remember this route for future invoices from this vendor.'

/**
 * WHO CREATED A RULE, AND FROM WHERE (owner, 2026-10-12 — D-350 amended), the
 * line above its sentence in the Edit Rule pop-up. Set once, at creation, and
 * never changed by an edit:
 *
 *   Created by Alex Morgan from an Override on invoice 79859, Oct 12, 2026, 4:16 PM.
 *   Created by Alex Morgan on Rules → Routing, Oct 12, 2026, 4:16 PM.
 *
 * A rule created before 2026-10-12 has no name kept, and reads without one.
 */
export function ruleCreatedLine(
  rule: Pick<OversightRule, 'source' | 'created_by_name' | 'override_invoice' | 'created_at'>,
): string {
  const who = rule.created_by_name?.trim() ? ` by ${rule.created_by_name.trim()}` : ''
  const when = formatDateTime(rule.created_at)
  if (rule.source === 'override') {
    const number = rule.override_invoice?.invoice_number?.trim()
    const invoice = number ? `invoice ${number}` : 'an invoice'
    return `Created${who} from an Override on ${invoice}, ${when}.`
  }
  return `Created${who} on Rules → Routing, ${when}.`
}
